import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, CalendarClock, CheckCircle2, CreditCard, Loader2, ShieldCheck } from 'lucide-react';
import {
  fetchCotisation,
  ouvrirPaiementCotisation,
  ouvrirPrelevementCotisation,
} from '../services/api';

/** Les centimes du serveur, en euros lisibles. */
function euros(centimes: number): string {
  return `${(centimes / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/** « 5 octobre 2026 » — une date qu'on lit sans la déchiffrer. */
function jour(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

type Echeance = { rang: number; montant: number; date: string; total?: number; statut?: string };
type Etalement = { nombre: number; libelle: string; echeances: Echeance[] };

/**
 * Le règlement d'une cotisation par la famille, sans compte.
 *
 * Un parent ne crée pas un compte pour payer quatre-vingts euros une fois
 * l'an. Le bureau lui transmet ce lien, et il suffit : la page dit qui, quelle
 * saison, combien reste dû, et ouvre le paiement.
 *
 * Elle ne montre rien d'autre du dossier. Le lien circule dans une
 * conversation de famille — il ne doit pas livrer une adresse ni une date de
 * naissance à qui le reçoit par ricochet.
 */
export default function CotisationPage() {
  const { reference = '' } = useParams();
  const [params] = useSearchParams();
  const [erreur, setErreur] = useState<string | null>(null);
  const [etalement, setEtalement] = useState<number>(0);

  const retour = params.get('paiement');
  const mandat = params.get('mandat');

  const query = useQuery({
    queryKey: ['cotisation', reference],
    queryFn: () => fetchCotisation(reference),
    retry: false,
    // Au retour de la banque, l'inscription du versement peut avoir une
    // seconde de retard : on relit tant que le solde n'a pas bougé.
    refetchInterval: (q) =>
      retour === 'succes' && q.state.data?.cotisation?.soldee !== true ? 2000 : false,
  });

  const payer = useMutation({
    mutationFn: () => ouvrirPaiementCotisation(reference),
    onSuccess: (d: any) => {
      if (d.url) window.location.href = d.url;
    },
    onError: (e: Error) => setErreur(e.message),
  });

  const prelever = useMutation({
    mutationFn: (nombre: number) => ouvrirPrelevementCotisation(reference, nombre),
    onSuccess: (d: any) => {
      if (d.url) window.location.href = d.url;
    },
    onError: (e: Error) => setErreur(e.message),
  });

  useEffect(() => {
    document.title = 'Règlement de cotisation';
  }, []);

  if (query.isPending) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-gray-300 mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Ce lien n'est plus valable</h1>
        <p className="text-sm text-gray-500">
          Rapprochez-vous du bureau de l'association : il pourra vous en transmettre un nouveau.
        </p>
      </div>
    );
  }

  const c = query.data.cotisation;

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8">
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">{c.association}</p>
        <h1 className="text-xl font-semibold text-gray-900 mb-1">Cotisation {c.saison}</h1>
        <p className="text-sm text-gray-500 mb-6">
          {c.adherent}
          {[c.categorie, c.discipline].filter(Boolean).length > 0 && (
            <> — {[c.categorie, c.discipline].filter(Boolean).join(' · ')}</>
          )}
        </p>

        {retour === 'succes' && c.soldee && (
          <p className="mb-5 flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Merci, votre règlement est enregistré. Le bureau en est informé, vous n'avez rien d'autre
              à faire.
            </span>
          </p>
        )}

        {retour === 'succes' && !c.soldee && (
          <p className="mb-5 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
            <Loader2 className="w-4 h-4 mt-0.5 shrink-0 animate-spin" />
            <span>Votre paiement est en cours d'enregistrement, cette page se met à jour toute seule.</span>
          </p>
        )}

        {mandat === 'signe' && (
          <p className="mb-5 flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Votre autorisation de prélèvement est enregistrée. Le premier prélèvement part
              aujourd'hui&nbsp;; comptez quelques jours pour qu'il apparaisse sur votre compte, comme
              pour tout prélèvement bancaire.
            </span>
          </p>
        )}

        {mandat === 'annule' && (
          <p className="mb-5 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
            L'autorisation n'a pas été signée. Rien n'a été prélevé, vous pouvez recommencer.
          </p>
        )}

        {retour === 'annule' && (
          <p className="mb-5 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
            Le paiement a été interrompu. Rien n'a été débité, vous pouvez recommencer.
          </p>
        )}

        {erreur && (
          <p className="mb-5 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{erreur}</span>
          </p>
        )}

        <div className="rounded-xl bg-gray-50 border border-gray-100 p-5 mb-6">
          <p className="text-xs uppercase tracking-wide text-gray-400">
            {c.soldee ? 'Cotisation réglée' : 'Reste à régler'}
          </p>
          <p className="text-3xl font-semibold text-gray-900 tabular-nums">
            {euros(c.soldee ? c.montant : c.reste)}
          </p>
          {c.regle > 0 && !c.soldee && (
            <p className="text-sm text-gray-500 mt-1">
              Déjà versé : {euros(c.regle)} sur {euros(c.montant)}
            </p>
          )}
          {c.echeance && !c.soldee && (
            <p className="text-sm text-gray-500 mt-1">À régler avant le {c.echeance}</p>
          )}
        </div>

        {c.soldee ? (
          <p className="text-sm text-gray-600">
            Cette cotisation est intégralement réglée. Le bureau peut vous délivrer une attestation de
            paiement si un organisme vous la demande.
          </p>
        ) : c.paiementEnLigne ? (
          <>
            {/*
              L'échéancier déjà en cours passe avant tout le reste : une
              famille qui revient sur la page veut d'abord savoir ce qui va
              être prélevé, et quand.
            */}
            {(c.echeancier ?? []).length > 0 && (
              <div className="mb-6 rounded-xl border border-gray-200 p-4">
                <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-gray-400 mb-3">
                  <CalendarClock className="w-3.5 h-3.5" />
                  Vos prélèvements
                </p>
                <ul className="space-y-1.5">
                  {(c.echeancier as Echeance[]).map((e) => (
                    <li key={e.rang} className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="text-gray-600">{jour(e.date)}</span>
                      <span className="flex items-center gap-2">
                        <span className="tabular-nums font-medium text-gray-900">
                          {euros(e.montant)}
                        </span>
                        <span
                          className={`text-xs ${
                            e.statut === 'payee'
                              ? 'text-green-600'
                              : e.statut === 'echouee'
                                ? 'text-red-600'
                                : 'text-gray-400'
                          }`}
                        >
                          {e.statut === 'payee'
                            ? 'encaissé'
                            : e.statut === 'en_cours'
                              ? 'en cours'
                              : e.statut === 'echouee'
                                ? 'rejeté'
                                : 'à venir'}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              onClick={() => payer.mutate()}
              disabled={payer.isPending}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-60"
            >
              {payer.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <CreditCard className="w-5 h-5" />
              )}
              Régler {euros(c.fraisBancaires?.demande ?? c.reste)} par carte
            </button>

            {/*
              LE DÉTAIL DU MONTANT, SOUS LE BOUTON ET AVANT LE CLIC.

              Quand le club reporte la commission bancaire, le bouton annonce
              122,09 € pour une cotisation de 120 €. Sans explication, cet écart
              ressemble à un supplément glissé au dernier moment — et c'est
              exactement là qu'on renonce, puis qu'on appelle le trésorier.

              Trois lignes suffisent : ce que coûte la licence, ce que prend la
              banque, et pourquoi. Elles ne s'affichent que si le club a fait ce
              choix ; sinon il n'y a rien à expliquer.
            */}
            {(c.fraisBancaires?.frais ?? 0) > 0 && (
              <div className="mt-3 rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 text-xs text-gray-500">
                <div className="flex items-baseline justify-between gap-3">
                  <span>Cotisation</span>
                  <span className="tabular-nums">{euros(c.fraisBancaires!.voulu)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-3 mt-1">
                  <span>Frais bancaires</span>
                  <span className="tabular-nums">{euros(c.fraisBancaires!.frais)}</span>
                </div>
                <p className="mt-2 pt-2 border-t border-gray-200">
                  Les frais prélevés par notre prestataire de paiement sont ajoutés au montant, pour
                  que l'association reçoive l'intégralité de votre cotisation. Ils n'existent pas si
                  vous réglez par chèque ou en espèces auprès du bureau.
                </p>
              </div>
            )}
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-400">
              <ShieldCheck className="w-3.5 h-3.5" />
              Paiement sécurisé — l'association ne voit jamais votre numéro de carte.
            </p>

            {/*
              L'étalement n'apparaît que si le club l'a ouvert, et chaque
              proposition montre ses dates et ses montants : on ne demande à
              personne d'autoriser des prélèvements qu'il découvrira sur son
              relevé.
            */}
            {(c.etalements ?? []).length > 0 && (c.echeancier ?? []).length === 0 && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-sm font-medium text-gray-900 mb-1">Ou payer en plusieurs fois</p>
                <p className="text-xs text-gray-500 mb-4">
                  Par prélèvement automatique sur votre compte bancaire. Vous pourrez l'interrompre
                  à tout moment auprès de votre banque.
                </p>

                <div className="space-y-2">
                  {(c.etalements as Etalement[]).map((e) => {
                    const ouvert = etalement === e.nombre;

                    return (
                      <div
                        key={e.nombre}
                        className={`rounded-xl border transition-colors ${
                          ouvert ? 'border-primary-300 bg-primary-50/40' : 'border-gray-200'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setEtalement(ouvert ? 0 : e.nombre)}
                          className="w-full flex items-baseline justify-between gap-3 px-4 py-3 text-left"
                        >
                          <span className="text-sm font-medium text-gray-900">{e.libelle}</span>
                          <span className="text-sm text-gray-500 tabular-nums">
                            {euros(e.echeances[0]?.montant ?? 0)}
                            {e.echeances.length > 1 && ' puis…'}
                          </span>
                        </button>

                        {ouvert && (
                          <div className="px-4 pb-4">
                            <ul className="mb-4 space-y-1 border-t border-gray-200/70 pt-3">
                              {e.echeances.map((ligne) => (
                                <li
                                  key={ligne.rang}
                                  className="flex items-baseline justify-between gap-3 text-sm"
                                >
                                  <span className="text-gray-600">{jour(ligne.date)}</span>
                                  <span className="tabular-nums font-medium text-gray-900">
                                    {euros(ligne.montant)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                            <button
                              onClick={() => prelever.mutate(e.nombre)}
                              disabled={prelever.isPending}
                              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-primary-600 text-primary-700 font-medium hover:bg-primary-50 disabled:opacity-60"
                            >
                              {prelever.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                              Autoriser ces prélèvements
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-600">
            Le paiement en ligne n'est pas ouvert pour cette association. Vous pouvez régler
            directement auprès d'un membre du bureau, en espèces, par chèque ou par virement.
          </p>
        )}

        <p className="mt-6 pt-5 border-t border-gray-100 text-xs text-gray-400 text-center">
          Une question ? Écrivez à {c.contact}
        </p>
      </div>
    </div>
  );
}
